/**
 * useTeamUnreadCount
 *
 * Listens to TEAM_UNREAD_COUNT socket events and exposes:
 * - totalUnread: total badge count for the header button
 * - unreadByUser: per-user unread count for the dropdown badges
 */

import { useState, useEffect } from 'react';
import { useSocket } from '@/contexts/SocketContext';
import { SOCKET_EVENTS } from '@troia-v3/types';
import type { TeamUnreadCountEvent } from '@troia-v3/types';

export const useTeamUnreadCount = () => {
  const { socket } = useSocket();
  const [totalUnread, setTotalUnread] = useState(0);
  const [unreadByUser, setUnreadByUser] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!socket) return;

    const handleUpdate = (event: TeamUnreadCountEvent) => {
      // Replace state entirely — event.byUser is a complete snapshot from Redis
      setTotalUnread(event.totalUnread);
      setUnreadByUser(event.byUser);
    };

    socket.on(SOCKET_EVENTS.TEAM_UNREAD_COUNT, handleUpdate);

    return () => {
      socket.off(SOCKET_EVENTS.TEAM_UNREAD_COUNT, handleUpdate);
    };
  }, [socket]);

  /**
   * Called when user opens a chat — clears local badge for that user.
   * The socket handler on the backend will also clear the Redis counter.
   */
  const clearUnreadForUser = (targetUserId: string) => {
    setUnreadByUser((prev) => {
      const next = { ...prev };
      delete next[targetUserId];
      const newTotal = Object.values(next).reduce((acc, v) => acc + v, 0);
      setTotalUnread(newTotal);
      return next;
    });
  };

  return { totalUnread, unreadByUser, clearUnreadForUser };
};
